import { useCallback, useRef, useState } from 'react'
import { IconUpload, IconInfo } from '../icons'

/**
 * 拖入区。
 *
 * 背景：本管线检测的是 2 px、2.67 px、4 px 量级的周期结构，任何重采样都会先一步
 *   抹掉这些结构，于是缩放后的图必然「诊断为干净」。所以服务不替用户缩放，
 *   像素数直接就是成本，超上限只能拒收。
 * 设计意图：把这条约束写在拖入区里而不是等报错才说。用户上传 4K 大图被拒时，
 *   如果此前没看到过上限，会认为是服务坏了。
 * 关键约束：
 *   1. 文件类型判定在服务端按文件头做，前端这里的 accept 只是选择器的过滤，
 *      不构成校验，不要依赖它。
 *   2. compact 是首屏那一份：它与右边的案例对比器并排，下面那条长说明会把两栏撑得
 *      一高一低，所以那条从首屏挪走了。它现在的落点是拒收卡（本文件下面那三句，
 *      只在图真的被挡下时出现）与工作页顶上的角标（Hero 的「全幅原像素分析 · 不缩放」）。
 *      中间在首屏挂过一张小卡，2026-09-10 连同另外两张一起删了。
 *      整页只有这一个上传入口，不要为了「显眼」再加一个按钮——两个入口会让人
 *      以为它们不是一件事。
 *   3. **门禁问在开门之前**（guard）。未注册的账号不能用这个工具（法规见 Gates 里
 *      RegisterFirstDialog 的注释），而那道门要挡在**系统文件选择器之前**：
 *      让人翻半天目录挑好一张图，再回来告诉他「你还没注册」，等于白使唤一趟。
 *      拖入那一路同样过 guard——拖进来的文件不经过选择器，只拦点击等于留了个后门。
 */
export function DropZone({ onPick, guard, compact = false }: {
  onPick: (f: File) => void
  /** 返回 false 表示这次不许上传（调用方自己去说明原因）。不传等于一律放行。 */
  guard?: () => boolean
  compact?: boolean
}) {
  const [over, setOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handle = useCallback((files: FileList | null) => {
    const f = files?.[0]
    if (f && (!guard || guard())) onPick(f)
  }, [onPick, guard])

  /** 点击与回车共用：过了门禁才把系统选择器叫出来。 */
  const open = useCallback(() => {
    if (guard && !guard()) return
    inputRef.current?.click()
  }, [guard])

  return (
    <div>
      <div
        className={`drop${over ? ' over' : ''}${compact ? ' drop-sm' : ''}`}
        onDragOver={e => { e.preventDefault(); setOver(true) }}
        onDragLeave={() => setOver(false)}
        onDrop={e => { e.preventDefault(); setOver(false); handle(e.dataTransfer.files) }}
        onClick={open}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') open() }}
      >
        <div className="drop-ic"><IconUpload size={26} /></div>
        <h3>把图片拖到这里，或点击选择</h3>
        <p>支持 PNG、JPEG、WebP，最大 40 MB、830 万像素（约 3840×2160）</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={e => handle(e.target.files)}
        />
      </div>

      {!compact && (
        <div className="drop-note">
          <IconInfo size={16} />
          <span>
            {'我们按原始像素做全幅分析，'}<b>不会替你缩放</b>
            {'。要检测的纹理周期只有 2 到 4 像素，一旦重采样就会先被抹掉，缩放后的图必然被判成干净的。'}
            {'所以超过上限的图只能请你先自己裁切。'}
          </span>
        </div>
      )}
    </div>
  )
}
